import { useState, useEffect, useRef } from 'react'
import { Loader2, Clock, RefreshCw } from 'lucide-react'
import { useI18n } from '../../i18n'
import { usePlan } from '../../hooks/use-plan'
import type { PayChannel, PayStatus } from '../../types'

interface PaymentQRCodeProps {
  orderId: string
  qrCodeUrl: string
  expireSeconds: number
  channel: PayChannel
  onStatusChange: (status: PayStatus) => void
}

export function PaymentQRCode({ orderId, qrCodeUrl, expireSeconds, channel, onStatusChange }: PaymentQRCodeProps) {
  const { t } = useI18n()
  const { pollPaymentStatus } = usePlan()
  const [remaining, setRemaining] = useState(expireSeconds)
  const [expired, setExpired] = useState(false)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const countdownRef = useRef<NodeJS.Timeout | null>(null)

  // 轮询支付状态（每2秒）
  useEffect(() => {
    intervalRef.current = setInterval(async () => {
      const status = await pollPaymentStatus(orderId)
      if (status === 'PAID') {
        clearInterval(intervalRef.current!)
        clearInterval(countdownRef.current!)
        onStatusChange('PAID')
      } else if (status === 'FAILED' || status === 'CLOSED') {
        clearInterval(intervalRef.current!)
        clearInterval(countdownRef.current!)
        onStatusChange(status)
      }
    }, 2000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [orderId])

  // 倒计时
  useEffect(() => {
    countdownRef.current = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!)
          clearInterval(intervalRef.current!)
          setExpired(true)
          onStatusChange('CLOSED')
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current)
    }
  }, [])

  const minutes = Math.floor(remaining / 60)
  const seconds = remaining % 60
  const channelName = channel === 'ALIPAY' ? t('支付宝', 'Alipay') : t('微信', 'WeChat')

  if (expired) {
    return (
      <div className="flex flex-col items-center py-6">
        <div className="w-48 h-48 rounded-2xl bg-gray-50 border border-black/[0.04] flex flex-col items-center justify-center mb-4">
          <Clock className="h-8 w-8 text-gray-300 mb-2" />
          <p className="text-sm text-gray-400">{t('二维码已过期', 'QR Code Expired')}</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="flex items-center gap-1.5 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded-xl transition-colors"
        >
          <RefreshCw className="h-4 w-4" />
          {t('刷新重试', 'Refresh & Retry')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center py-4">
      <div className="w-48 h-48 rounded-2xl bg-white border border-black/[0.04] p-2 mb-4 overflow-hidden">
        <img
          src={qrCodeUrl}
          alt="Payment QR Code"
          className="w-full h-full object-contain"
        />
      </div>
      <p className="text-sm text-[#1a1a1a] font-medium mb-1">
        {t(`请使用${channelName}扫码支付`, `Scan with ${channelName} to pay`)}
      </p>
      <div className="flex items-center gap-1 text-xs text-gray-400">
        <Clock className="h-3.5 w-3.5" />
        <span>{t('剩余', 'Expires in')} {minutes}:{seconds.toString().padStart(2, '0')}</span>
      </div>
      <div className="flex items-center gap-1.5 mt-3 text-xs text-gray-400">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>{t('等待支付中...', 'Waiting for payment...')}</span>
      </div>
    </div>
  )
}
